import { Component, computed, inject, input } from '@angular/core';
import { FormGroup, AbstractControl, FormControl } from '@angular/forms';
import { FormSchema, Property, Properties } from '../../models/form.model';
import { FormService } from '../../services/form.service';
import { ButtonComponent } from '@ui';
import { ControlSelectComponent } from '../select/select.component';

/**
 * Component for dynamically generating forms based on a given schema.
 */
@Component({
  imports: [ControlSelectComponent, ButtonComponent],
  selector: 'form-dynamic',
  templateUrl: './form-dynamic.component.html',
})
export class FormDynamicComponent {
  formSchema = input.required<FormSchema>({});
  submitText = input<string>('Submit');

  // Inject FormService and initialize reactive form schema
  private formService = inject(FormService);

  // Reactive form group instance initialized based on form schema
  form = computed(() => this.formService.createForm(this.formSchema()))

  /**
   * Retrieves the form control by control ID.
   * @param form - The form group instance.
   * @param controlId - The control ID to locate.
   * @returns The form control or null if not found.
   */
  getControl(form: FormGroup, controlId: string): FormControl {
    return this.formService.getControl(form, controlId) as FormControl;
  }

  /**
   * Retrieves the properties based on the given properties set.
   * @param properties - The set of properties to extract.
   * @returns The array of property objects.
   */
  getProperties(properties: Properties): Array<{ id: string } & Property> {
    return this.formService.getProperties(properties);
  }

  /**
   * Handles form submission by validating and processing the form data.
   * Displays validation errors if any.
   */
  onSubmit(): void {
    if (this.form().valid) {
      console.info('Form successfully submitted:', this.form().value);
    } else {
      this.handleFormErrors();
    }
  }

  /**
   * Handles and logs form validation errors.
   */
  private handleFormErrors(): void {
    const errors = this.formService.getFormErrors(this.form());
    console.warn('Form submission failed. Validation errors:', errors);
  }
}
